import { Button, CloseSVG, Modal, useModal } from "@newsela/angelou";
import Image from "next/image";
import PropTypes from "prop-types";

import {
  actionNames,
  eventTypes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import CHALLENGES_ROUTES from "@/challenges/constants/routes.constants";

import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import LearnMoreCoin from "../../../public/images/icons/learnMoreCoin.js";
import learnMoreArticleBadge from "../../../public/images/icons/learnMoreArticleBadge.png";
import {
  articleTextDescription,
  articleTextTitle,
  articleTextWrapper,
  bodyDescriptionCss,
  bodyTitleCss,
  buttonCss,
  buttonWrapper,
  closeButtonCss,
  guidedHighlightImage,
  headerTitleCss,
  imageCss,
  imagesWrapper,
  infoBlockWrapper,
  linkStyle,
  modalBodyCss,
  modalContainerCss,
  modalCss,
  modalFooterCss,
  modalHeaderCss,
  powerWordImage,
  quizzesImage,
  tokensWrapper,
} from "./ChallengeLearnMoreModal.style";

const learnMoreImages = () => {
  return (
    <div css={imagesWrapper}>
      <div css={quizzesImage} />
      <div css={powerWordImage} />
      <div css={guidedHighlightImage} />
    </div>
  );
};

const learnMoreArticle = () => {
  return (
    <div css={imagesWrapper}>
      <div css={imageCss}>
        <Image src={learnMoreArticleBadge} />
      </div>
      <div css={articleTextWrapper}>
        <h2 css={articleTextTitle}>The monthly challenge badge</h2>
        <p css={articleTextDescription}>
          You’ll also earn a monthly challenge badge when you reach your reading
          goal.
        </p>
      </div>
    </div>
  );
};

const learnMoreTokens = () => {
  return (
    <div css={tokensWrapper}>
      <div css={imagesWrapper}>
        <LearnMoreCoin />
        <div css={articleTextWrapper}>
          <h2 css={articleTextTitle}>Tokens</h2>
          <p css={articleTextDescription}>
            To earn tokens, students will complete specified activities that are
            paired with the articles.
          </p>
        </div>
      </div>
      <div css={imagesWrapper}>
        <div css={imageCss}>
          <Image src={learnMoreArticleBadge} />
        </div>
        <div css={articleTextWrapper}>
          <h2 css={articleTextTitle}>
            How do students complete the challenge?
          </h2>
          <p css={articleTextDescription}>
            To complete the challenge and earn a monthly badge, Elementary
            Students must read a minimum of five articles and Middle school and
            high school students must read a minimum of 9 articles.
          </p>
        </div>
      </div>
    </div>
  );
};

const studentContentInfo = [
  {
    title: "What is it?",
    description:
      "The monthly reading challenge is an optional activity you can participate in to earn tokens.",
    child: null,
  },
  {
    title: "How do I earn tokens?",
    description:
      "To earn tokens, read a challenge article and complete its activities. Earn up to 150 tokens for quizzes and up to 50 tokens for Power Words.",
    child: learnMoreImages,
  },
  {
    title: "Do I have to read all the articles?",
    description:
      "Nope ...but the more you read, the more activities you can complete (and the more tokens you can earn).",
    child: learnMoreArticle,
  },
  {
    title: "What if I don’t finish the entire challenge?",
    description:
      "No problem. When the challenge closes, you’ll keep the tokens you earned and can redeem them for rewards.",
    child: null,
  },
  {
    title: "What can I redeem my tokens for?",
    description:
      "Soon, you’ll be able to redeem your tokens for some great rewards. Want to help us decide what they should be?",
    link: { href: CHALLENGES_ROUTES.REWARDS.route, text: "Let us know." },
    child: null,
  },
];

const teacherContentInfo = [
  {
    title: "What is it?",
    description:
      "Your students can participate in an optional monthly reading challenge to encourage independent reading and promote student choice. Students can read articles that interest them and earn tokens and badges as they go.",
    child: null,
  },
  {
    title: "How do students earn tokens and badges?",
    description: "",
    child: learnMoreTokens,
  },
  {
    title: "Do students have to read all the articles?",
    description:
      "No, students can read as many articles as they want to. That said, the more articles they read and activities they complete, the more tokens they’ll earn.",
    child: null,
  },
  {
    title: "What if students don’t complete the challenge?",
    description:
      "Students will keep the tokens they earned for completing activities but will not earn a monthly reading challenge badge. However, we encourage students to read independently to achieve a monthly badge even if they read the minimum number of articles.",
    child: null,
  },
  {
    title: "Can I tell my students what to read?",
    description:
      "While you can tell students exactly what to read, participation in the monthly reading challenge (and selecting which articles to read) is meant to be student-led.",
    child: null,
  },
  {
    title: "Why can’t I assign this content?",
    description:
      "This monthly reading challenge is meant to motivate self-led reading and promote student choice—so it’s optional for students to participate in.",
    child: null,
  },
];

const ChallengeLearnMoreModalHeader = (setActive) => (
  <div css={modalHeaderCss}>
    <h3 css={headerTitleCss}>Monthly reading challenge</h3>
    <Button
      __cssFor={{ root: closeButtonCss }}
      flavor="INCOGNITO"
      ariaProps={{
        "aria-label": "Close Modal Button",
      }}
      icon={{ SvgComponent: CloseSVG }}
      onClick={() => setActive(false)}
    />
  </div>
);

const infoBlock = (title, description, child, link, key) => {
  return (
    <div key={key} css={infoBlockWrapper}>
      <h2 css={bodyTitleCss}>{title}</h2>
      <p css={bodyDescriptionCss(child)}>
        {description}{" "}
        {link && (
          <a css={linkStyle} href={link.href}>
            {link.text}
          </a>
        )}
      </p>
      {child && child()}
    </div>
  );
};

const ChallengeLearnMoreModalBody = (isTeacher) => {
  const content = isTeacher ? teacherContentInfo : studentContentInfo;
  return (
    <div css={modalBodyCss}>
      {content.map((content, index) =>
        infoBlock(
          content.title,
          content.description,
          content.child,
          content?.link,
          index,
        ),
      )}
    </div>
  );
};

const ChallengeLearnMoreModalFooter = (setActive) => (
  <div css={modalFooterCss}>
    <Button
      flavor="PRIMARY"
      onClick={() => setActive(false)}
      __cssFor={{ root: buttonWrapper }}
    >
      Got it
    </Button>
  </div>
);

const LearnMoreModal = (setActive) => {
  const { data: user } = useUser();
  const { isTeacher } = getUserProperties(user);

  return (
    <div css={modalContainerCss}>
      {ChallengeLearnMoreModalHeader(setActive)}
      {ChallengeLearnMoreModalBody(isTeacher)}
      {ChallengeLearnMoreModalFooter(setActive)}
    </div>
  );
};

const ChallengeLearnMoreModal = ({ buttonStyle, buttonFlavor }) => {
  const [modalProps, setActive, triggerRef] = useModal(false);
  const ButtonWithTrackEvent = withTrackEvent(Button);
  const eventProperties = {
    objectType: objectTypes.user,
    actionName: actionNames.interact,
    event_type: eventTypes.click,
    streamProperties: {
      component_type: "ChallengeLearnMoreModal",
      description: "Open IRC Learn More Modal",
      // user_agent: navigator.userAgent,
    },
  };
  const btnRootStyle = buttonStyle || buttonCss;
  const btnFlavor = buttonFlavor || Button.flavors.incognito;

  return (
    <ButtonWithTrackEvent
      __cssFor={{ root: btnRootStyle }}
      flavor={btnFlavor}
      {...eventProperties}
      onClick={() => setActive(true)}
      ref={triggerRef}
      dataProps={{ "data-qa-selector": "IRC: Open Learn more modal" }}
    >
      Learn more
      <Modal
        {...modalProps}
        onBackgroundClick={() => setActive(false)}
        ariaProps={{
          "aria-labelledby": "modal_label",
        }}
        __cssFor={{ root: modalCss }}
      >
        {LearnMoreModal(setActive)}
      </Modal>
    </ButtonWithTrackEvent>
  );
};

ChallengeLearnMoreModal.propTypes = {
  buttonStyle: PropTypes.object,
  buttonFlavor: PropTypes.string,
};

export default ChallengeLearnMoreModal;
